import React, { useState } from "react";
import { X, Shield, Mail, Plus, Trash2 } from "lucide-react";
import { Site, MultipleSitesAlertRuleFormData, AlertRule } from "../../types";
import { OrgWithPermission } from "../../services/organization.ts";

// Extend the formData type to include email addresses
interface ExtendedFormData extends MultipleSitesAlertRuleFormData {
  emailAddresses: string[];
}

interface AlertRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  formData: ExtendedFormData;
  setFormData: React.Dispatch<React.SetStateAction<ExtendedFormData>>;
  selectedOrganization: OrgWithPermission;
  allSites: Site[];
  editingAlertRule: AlertRule | null;
  modalLoading: boolean;
}

const metricOptions = [
  { value: "soc", label: "Battery SOC (%)" },
  { value: "battery_voltage", label: "Battery Voltage (V)" },
  { value: "solar_power", label: "Solar Power (W)" },
  { value: "status", label: "Device Status" },
];

const AlertRuleModal: React.FC<AlertRuleModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  formData,
  setFormData,
  selectedOrganization,
  allSites,
  editingAlertRule,
  modalLoading,
}) => {
  const [newEmail, setNewEmail] = useState("");

  // Email validation function
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = () => {
    if (newEmail.trim() && isValidEmail(newEmail.trim())) {
      if (!formData.emailAddresses.includes(newEmail.trim())) {
        setFormData({
          ...formData,
          emailAddresses: [...formData.emailAddresses, newEmail.trim()],
        });
        setNewEmail("");
      }
    }
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setFormData({
      ...formData,
      emailAddresses: formData.emailAddresses.filter(
        (email) => email !== emailToRemove
      ),
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold">
              {editingAlertRule
                ? "Edit Alert Rule"
                : "Create Alert Rule for Multiple Sites"}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Organization: {selectedOrganization.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-6">
          {/* Alert Rule Definition */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-4">
              Alert Rule Configuration
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rule Name *
                </label>
                <input
                  type="text"
                  value={formData.alertRule.name}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      alertRule: {
                        ...formData.alertRule,
                        name: e.target.value,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 transition-colors"
                  placeholder="Enter rule name"
                  required
                />
              </div>

              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.alertRule.description}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      alertRule: {
                        ...formData.alertRule,
                        description: e.target.value,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 transition-colors"
                  rows={2}
                  placeholder="Enter rule description (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Metric
                </label>
                <select
                  value={formData.alertRule.condition.metric}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      alertRule: {
                        ...formData.alertRule,
                        condition: {
                          ...formData.alertRule.condition,
                          metric: e.target.value as any,
                        },
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 transition-colors"
                >
                  {metricOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Operator
                </label>
                <select
                  value={formData.alertRule.condition.operator}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      alertRule: {
                        ...formData.alertRule,
                        condition: {
                          ...formData.alertRule.condition,
                          operator: e.target.value as any,
                        },
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 transition-colors"
                >
                  <option value="gt">Greater than</option>
                  <option value="lt">Less than</option>
                  <option value="eq">Equal to</option>
                  <option value="gte">Greater than or equal</option>
                  <option value="lte">Less than or equal</option>
                  <option value="ne">Not equal</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Threshold
                </label>
                <input
                  type="number"
                  value={formData.alertRule.condition.threshold}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      alertRule: {
                        ...formData.alertRule,
                        condition: {
                          ...formData.alertRule.condition,
                          threshold: parseFloat(e.target.value) || 0,
                        },
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 transition-colors"
                  step="0.01"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Severity
                </label>
                <select
                  value={formData.alertRule.severity}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      alertRule: {
                        ...formData.alertRule,
                        severity: e.target.value as any,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 transition-colors"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
          </div>

          {/* Email Notification Recipients */}
          <div className="bg-orange-50 rounded-lg p-4">
            <h4 className="text-md font-medium text-orange-900 mb-4 flex items-center">
              <Mail className="w-5 h-5 mr-2" />
              Email Notifications
            </h4>
            <p className="text-sm text-orange-700 mb-4">
              Add email addresses that should receive notifications when this
              alert is triggered.
            </p>

            {/* Add Email Input */}
            <div className="mb-4">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500 transition-colors"
                  placeholder="Enter email address"
                />
                <button
                  type="button"
                  onClick={handleAddEmail}
                  disabled={!newEmail.trim() || !isValidEmail(newEmail.trim())}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </button>
              </div>
              {newEmail && !isValidEmail(newEmail) && (
                <p className="text-xs text-red-600 mt-1">
                  Please enter a valid email address
                </p>
              )}
            </div>

            {/* Email List */}
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-2 pb-2 border-b border-orange-200">
                <span className="text-sm font-medium text-orange-900">
                  Recipients ({formData.emailAddresses.length})
                </span>
                {formData.emailAddresses.length > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        emailAddresses: [],
                      })
                    }
                    className="text-xs text-orange-600 hover:text-orange-800"
                  >
                    Clear All
                  </button>
                )}
              </div>

              {formData.emailAddresses.length === 0 ? (
                <p className="text-sm text-orange-600 text-center py-4">
                  No email addresses added yet.
                </p>
              ) : (
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {formData.emailAddresses.map((email, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-white rounded-md p-2 border border-orange-200"
                    >
                      <span className="text-sm text-gray-900">{email}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveEmail(email)}
                        className="text-red-600 hover:text-red-800 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Site Selection */}
          {!editingAlertRule && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-md font-medium text-blue-900 mb-4">
                Site Selection
              </h4>
              <p className="text-sm text-blue-700 mb-4">
                Select one or more sites where this alert rule will be applied.
                The rule will be created for all devices in the selected sites.
              </p>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                <div className="flex items-center justify-between mb-2 pb-2 border-b border-blue-200">
                  <span className="text-sm font-medium text-blue-900">
                    Select Sites ({formData.selectedSites.length} selected)
                  </span>
                  <div className="space-x-2">
                    <button
                      type="button"
                      onClick={() =>
                        setFormData({
                          ...formData,
                          selectedSites: allSites.map((site) => site.id),
                        })
                      }
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData({
                          ...formData,
                          selectedSites: [],
                        })
                      }
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                {allSites.map((site) => (
                  <div key={site.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`site-${site.id}`}
                      checked={formData.selectedSites.includes(site.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            selectedSites: [...formData.selectedSites, site.id],
                          });
                        } else {
                          setFormData({
                            ...formData,
                            selectedSites: formData.selectedSites.filter(
                              (id) => id !== site.id
                            ),
                          });
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={`site-${site.id}`}
                      className="ml-3 block text-sm text-gray-900"
                    >
                      <div className="font-medium">{site.name}</div>
                      <div className="text-xs text-gray-500">
                        {site.type} • {site.deviceCount} devices
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary */}
          {formData.selectedSites.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <Shield className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-green-900 mb-1">
                    Rule Summary
                  </h4>
                  <p className="text-sm text-green-700">
                    {editingAlertRule
                      ? `This alert rule will be updated for the "${editingAlertRule.site.name}" site.`
                      : `This alert rule will be created for ${formData.selectedSites.length} site(s). 
                        All devices in the selected sites will automatically inherit this rule.`}
                  </p>
                  {formData.emailAddresses.length > 0 && (
                    <p className="text-sm text-green-700 mt-2">
                      Email notifications will be sent to{" "}
                      {formData.emailAddresses.length} recipient(s).
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              disabled={modalLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
              disabled={modalLoading || formData.selectedSites.length === 0}
            >
              {modalLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {editingAlertRule ? "Updating..." : "Creating..."}
                </div>
              ) : editingAlertRule ? (
                "Update Rule"
              ) : (
                `Create Rule for ${formData.selectedSites.length} Site(s)`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AlertRuleModal;
